import { FolderKanban, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate } from '../../../shared/format'
import type { CatalogModuleView, ProjectSummary } from '../../types'
import { InclusionBadge, ProjectIcon, ProjectStatusBadge } from '../ProjectBits'
import type { IntegrateTarget } from './IntegrateModal'
import { usePriceFormat } from '../../usePriceFormat'

type Props = { module: CatalogModuleView; projects: ProjectSummary[]; canWrite: boolean; onIntegrate: (t: IntegrateTarget) => void }

/** Modülün entegre edildiği projeler: zorunluluk, projedeki fiyat, eklenme tarihi. */
export function ModuleProjects({ module: m, projects, canWrite, onIntegrate }: Props) {
  const { t } = useTranslation()
  const fmtPrice = usePriceFormat()
  const byId = new Map(projects.map((p) => [p.id, p]))
  const rows = m.usage.flatMap((u) => {
    const p = byId.get(u.projectId)
    return p ? [{ ...u, project: p }] : []
  })
  const free = projects.filter((p) => !m.usage.some((u) => u.projectId === p.id))

  const price = (value: string) => (
    <span className="tabular-nums">
      {fmtPrice(value)}
      {value !== m.price ? <span className="ml-1 text-[11px] text-muted-foreground">({t('projects.catalog.detail.special')})</span> : null}
    </span>
  )
  const projectCell = (p: ProjectSummary) => (
    <Link to={`/projects/${p.id}`} className="flex min-w-0 items-center gap-2.5 hover:text-primary">
      <ProjectIcon icon={p.icon} color={p.color} className="size-8 rounded-lg" />
      <span className="min-w-0">
        <span className="block truncate font-medium">{p.name}</span>
        <span className="block truncate text-xs text-muted-foreground">{p.description}</span>
      </span>
    </Link>
  )
  const edit = (projectId: string) =>
    canWrite ? (
      <Button size="sm" variant="ghost" onClick={() => onIntegrate({ kind: 'edit', moduleId: m.id, projectId })}>
        {t('common.edit')}
      </Button>
    ) : null

  return (
    <section className="@container overflow-hidden rounded-xl border bg-card shadow-card">
      <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <FolderKanban className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="font-semibold">{t('projects.catalog.detail.projects')}</h2>
        <Badge tone="info">{rows.length}</Badge>
        {canWrite && m.active && free.length ? (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => onIntegrate({ kind: 'create', moduleId: m.id })}>
            <Plus />
            {t('projects.catalog.addToProject')}
          </Button>
        ) : null}
      </header>
      {rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('projects.catalog.notUsed')}</p>
      ) : (
        <>
          <ul className="divide-y @3xl:hidden">
            {rows.map((r) => (
              <li key={r.projectId} className="space-y-2 p-4">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">{projectCell(r.project)}</div>
                  {edit(r.projectId)}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <InclusionBadge requirement={r.requirement} />
                  <ProjectStatusBadge status={r.project.status} />
                  <span className="ml-auto">{price(r.price)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t('projects.catalog.detail.addedAt', { date: fmtDate(r.addedAt) })}</p>
              </li>
            ))}
          </ul>
          <div className="hidden @3xl:block">
            <Table>
              <THead>
                <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
                  <TH>{t('projects.catalog.project')}</TH>
                  <TH>{t('projects.modules.columns.status')}</TH>
                  <TH className="text-right">{t('projects.catalog.fields.projectPrice')}</TH>
                  <TH className="hidden @4xl:table-cell">{t('projects.fields.status')}</TH>
                  <TH className="text-right">{t('projects.catalog.detail.added')}</TH>
                  {canWrite ? <TH className="w-20" aria-label={t('projects.work.columns.actions')} /> : null}
                </TR>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <TR key={r.projectId}>
                    <TD className="max-w-80">{projectCell(r.project)}</TD>
                    <TD>
                      <InclusionBadge requirement={r.requirement} />
                    </TD>
                    <TD className="whitespace-nowrap text-right">{price(r.price)}</TD>
                    <TD className="hidden @4xl:table-cell">
                      <ProjectStatusBadge status={r.project.status} />
                    </TD>
                    <TD className="whitespace-nowrap text-right text-sm tabular-nums text-muted-foreground">{fmtDate(r.addedAt)}</TD>
                    {canWrite ? <TD className="text-right">{edit(r.projectId)}</TD> : null}
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </>
      )}
    </section>
  )
}
